import { type ThemeColorsV2 } from '#app/theme/index.js';
import {
  type CheckboxProps,
  CheckboxShape,
  type CheckboxSize,
} from './types.js';
import { CheckIcon } from '@contra/icons/icons';
import { forwardRef, type MouseEvent, useEffect, useState } from 'react';
import { css, styled } from 'styled-components';

const StyledHiddenCheckbox = styled.input<{ readOnly?: boolean }>`
  ${({ readOnly }) => css`
    opacity: 0;
    -moz-appearance: none !important;
    width: 100%;
    height: 100%;
    margin: 0;
    top: 0;
    left: 0;
    position: absolute;
    ${readOnly
      ? css`
          cursor: default;
        `
      : css`
          cursor: pointer;
        `}

    &:disabled {
      opacity: 0;
      cursor: not-allowed;
    }
  `}
`;

const StyledSwitchContainer = styled.div<{
  $readOnly?: boolean;
  $size: CheckboxSize;
  checkedColor: ThemeColorsV2;
  isChecked: boolean;
  uncheckedColor: ThemeColorsV2;
}>`
  ${({
    $readOnly,
    $size,
    checkedColor,
    isChecked,
    theme,
    uncheckedColor,
  }) => css`
    ${$readOnly
      ? css`
          cursor: default;
        `
      : css`
          cursor: pointer;
        `}

    background-color: ${isChecked
      ? theme.colorsV2[checkedColor]
      : theme.colorsV2[uncheckedColor]};
    border-radius: 31px;

    ${$size === 'small'
      ? css`
          width: 30px;
          height: 16px;
        `
      : css`
          width: 44px;
          height: 24px;
        `}

    position: relative;
    display: flex;
    align-items: center;

    transition: background-color 0.15s ease-out;

    &:active > div {
      width: 24px;
    }
  `}
`;

const StyledSwitchHandle = styled.div<{
  $size: CheckboxSize;
  isChecked: boolean;
}>`
  ${({ $size, theme }) => css`
    ${$size === 'small'
      ? css`
          width: 12px;
          height: 12px;
        `
      : css`
          width: 18px;
          height: 18px;
        `}

    background-color: ${theme.colorsV2.white};
    border-radius: 18px;

    position: absolute;
    left: 3px;

    transition: 0.15s ease-out;
  `}

  ${({ isChecked }) =>
    isChecked &&
    css`
      left: calc(100% - 3px);
      transform: translateX(-100%);
    `}
`;

const StyledCheckBoxContainer = styled.div<{
  $hasError: boolean;
  $indeterminate: boolean;
  $isChecked: boolean;
  $isDisabled: boolean;
  $readOnly?: boolean;
  $shape?: CheckboxShape;
  $size: CheckboxSize;
}>`
  ${({
    $hasError,
    $indeterminate,
    $isChecked,
    $isDisabled,
    $readOnly,
    $shape,
    $size,
    theme,
  }) => css`
    position: relative;

    min-width: 24px;
    min-height: 24px;
    width: 24px;
    height: 24px;

    border: 2px solid ${theme.colorsV2.gray50};

    ${$shape === 'rounded'
      ? css`
          border-radius: 100%;
        `
      : css`
          border-radius: 4px;
        `}

    display: flex;
    align-items: center;
    justify-content: center;

    ${$readOnly
      ? css`
          cursor: default;
        `
      : css`
          cursor: pointer;
        `}

    transition: all 0.1s ease-in;

    &:hover {
      border-color: ${$isDisabled ? undefined : theme.colorsV2.gray90};
    }

    ${$size === 'small' &&
    css`
      min-width: 16px;
      min-height: 16px;
      width: 16px;
      height: 16px;
      border: 1.5 solid ${theme.colorsV2.gray50};

      ${$shape === 'rounded'
        ? css`
            border-radius: 100%;
          `
        : css`
            border-radius: 3px;
          `}
    `}

    ${$isChecked &&
    css`
      color: ${$isDisabled ? theme.colorsV2.gray40 : undefined};
      background-color: ${$isDisabled
        ? theme.colorsV2.gray20
        : theme.colorsV2.gray90};
      border-color: ${$isDisabled
        ? theme.colorsV2.gray20
        : theme.colorsV2.gray90};
    `};

    ${$indeterminate &&
    css`
      border-color: ${theme.colorsV2.gray90};
    `}

    ${$hasError &&
    css`
      border-color: ${theme.colorsV2.red50}};
    `}
  `}
`;

const StyledIndeterminateFiller = styled.div`
  background: #f2c94c;
  border-radius: 1px;

  width: 12px;
  height: 12px;
`;

const StyledCheckmarkIconContainer = styled(CheckIcon)<{
  $isChecked?: boolean;
  $isDisabled?: boolean;
}>`
  transform-origin: center center;
  transform: scale(0.5, 0.5);
  transition: all 0.2s ease-out;

  ${({ $isChecked, $isDisabled, theme }) => {
    const color = $isDisabled ? theme.colorsV2.gray40 : theme.colorsV2.white;
    return $isChecked
      ? css`
          stroke: ${color};
          color: ${color};
          transform: scale(1, 1);
          opacity: 1;
        `
      : css`
          opacity: 0;
        `;
  }}
`;

const Layout = styled.div`
  position: relative;
`;

const Check = styled.div`
  ${({ theme }) => css`
    display: flex;
    margin: auto;
    align-items: flex-start;

    ${theme.mediaQueries.sm} {
      align-items: center;
    }
  `}
`;

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      $size = 'default',
      checked,
      checkedColor = 'green50',
      children,
      className,
      disabled,
      hasError = false,
      id,
      indeterminate = false,
      isToggle = false,
      name,
      onClick,
      preChecked = false,
      readOnly,
      shape,
      uncheckedColor = 'gray50',
      ...props
    }: CheckboxProps,
    ref,
  ) => {
    const [isChecked, setIsChecked] = useState(preChecked || false);

    const handleClick = (event: MouseEvent<HTMLInputElement>) => {
      if (!indeterminate) {
        setIsChecked(event.currentTarget.checked);
        if (onClick) {
          onClick(event);
        }
      }
    };

    useEffect(() => {
      // keep in sync with checked value
      if (checked !== undefined && isChecked !== checked) {
        setIsChecked(checked);
      }
    }, [checked, isChecked]);

    return (
      <Layout className={className}>
        {isToggle ? (
          <StyledSwitchContainer
            $readOnly={readOnly}
            $size={$size}
            checkedColor={checkedColor}
            isChecked={isChecked}
            uncheckedColor={uncheckedColor}
          >
            <StyledSwitchHandle
              $size={$size}
              isChecked={isChecked}
            />
            {children}
          </StyledSwitchContainer>
        ) : (
          <Check>
            <StyledCheckBoxContainer
              {...{
                $hasError: hasError,
                $indeterminate: indeterminate,
                $isChecked: isChecked,
                $isDisabled: disabled ?? false,
                $readOnly: readOnly,
                $shape: shape,
                $size,
                disabled,
              }}
            >
              {!indeterminate && (
                <StyledCheckmarkIconContainer
                  $isChecked={isChecked}
                  $isDisabled={disabled}
                  size={16}
                />
              )}
              {indeterminate && !isChecked && <StyledIndeterminateFiller />}
            </StyledCheckBoxContainer>
            {children}
          </Check>
        )}
        <StyledHiddenCheckbox
          defaultChecked={isChecked}
          id={id}
          name={name}
          onClick={(event: MouseEvent<HTMLInputElement>) => handleClick(event)}
          readOnly={readOnly}
          type="checkbox"
          {...{ indeterminate, ...props, checked, disabled, ref }}
        />
      </Layout>
    );
  },
);
